import { call, put, take, fork } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as types from 'src/state/ducks/orderBread/types';
import * as actions from 'src/state/ducks/orderBread/actions';
import * as notificationActions from 'src/state/ducks/notification/actions';
import * as api from 'src/state/ducks/orderBread/api';
import { Status } from 'src/constants/Interface';
import ValidationManager from 'src/firebase/db/ValidationManager';
import { OrderBreadEntity, orderBreadEntityKeys } from 'src/firebase/db/OrderBreads';
dayjs.extend(isSameOrAfter);

function* validateOrderBread() {
  while (true) {
    const { payload } = yield take(types.VALIDATE_ORDER_BREAD);

    // 入力された値を検証する
    const form = payload as OrderBreadEntity;
    const validator = new OrderBreadValidationManager();

    // 入力必須
    const IS_REQUIRED = [
      'stock',
      'maximum_order',
      'required_baking_minutes',
      'last_pickup_time',
      'can_order_time',
    ];

    orderBreadEntityKeys.forEach((key) => {
      if (IS_REQUIRED.includes(key)) {
        validator.isRequired(key, form[key]);
      }

      const times = {
        requiredBakingMinutes: form['required_baking_minutes'],
        canOrderTime: form['can_order_time'],
        lastPickupTime: form['last_pickup_time'],
      };

      switch (key) {
        case 'can_order_time': {
          validator.isValidCanOrderTime(times);
          break;
        }
        case 'last_pickup_time': {
          validator.isValidLastPickupTime(times);
          break;
        }
      }
    });

    if (validator.isPass()) {
      yield put(push(`/order/confirm/?bread_id=${payload.bread_id}`));
    } else {
      const errors = validator.throwValidateError(validator.getErrors());
      yield put(actions.failValidateOrderBread(errors));
    }
  }
}

function* getOrderBread() {
  while (true) {
    const { payload } = yield take(types.GET_ORDER_BREAD);
    try {
      const res = yield call(api.getOrderBread, payload.id);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetOrderBread(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetOrderBread(res));
      }
    } catch (err) {
      yield put(
        actions.failGetOrderBread({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getOrderBreads() {
  while (true) {
    const { payload } = yield take(types.GET_ORDER_BREADS);
    console.log(payload);
    try {
      const res = yield call(api.getOrderBreads, payload.shopId);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetOrderBreads(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetOrderBreads(res));
      }
    } catch (err) {
      yield put(
        actions.failGetOrderBreads({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getNotificationOrderBreads() {
  while (true) {
    const { payload } = yield take(types.GET_NOTIFICATION_ORDER_BREADS);
    console.log('payload', payload);
    try {
      const res = yield call(
        api.getNotificationOrderBreads,
        payload.shopId,
        payload.notificationId,
      );
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetNotificationOrderBreads(res));
      }
    } catch (err) {
      yield put(
        actions.failGetNotificationOrderBreads({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getDayOrderBreads() {
  while (true) {
    const { payload } = yield take(types.GET_DAY_ORDER_BREADS);
    console.log('payload', payload);
    try {
      const res = yield call(
        api.getDayOrderBreads,
        payload.shopId,
        payload.startDate,
        payload.endDate,
      );
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetDayOrderBreads(res));
      }
    } catch (err) {
      yield put(
        actions.failGetDayOrderBreads({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* createOrderBread() {
  while (true) {
    const { payload } = yield take(types.CREATE_ORDER_BREAD);
    try {
      const res = yield call(api.createOrderBread, payload);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successCreateOrderBread(res));
        yield put(push('/order/complete'));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failCreateOrderBread(res));
      }
    } catch (err) {
      yield put(
        actions.failCreateOrderBread({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* deleteOrderBread() {
  while (true) {
    const { payload } = yield take(types.DELETE_ORDER_BREAD);
    try {
      const res = yield call(api.deleteOrderBread, payload);
      const { status, data: orderBread } = res;
      if (status === Status.SUCCESS) {
        yield put(actions.successDeleteOrderBread(res));

        const currentUrl = window.location.href;
        const lastURLSegment = currentUrl.substr(currentUrl.lastIndexOf('/') + 1);
        if (dayjs(lastURLSegment, 'YYYY-MM-DD').isValid()) {
          // 一覧ページの場合は再描画する
          const date = dayjs(lastURLSegment);
          yield put(
            notificationActions.getDayNotifications({
              shopId: orderBread.shop_id,
              startDate: date.startOf('date').toDate(),
              endDate: date.endOf('date').toDate(),
              isReservation: false,
            }),
          );
        }
      } else if (status === Status.INVALID) {
        yield put(actions.failDeleteOrderBread(res));
      }
    } catch (err) {
      yield put(
        actions.failDeleteOrderBread({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

const orderBread = [
  getOrderBread(),
  getOrderBreads(),
  validateOrderBread(),
  createOrderBread(),
  deleteOrderBread(),
  fork(getDayOrderBreads),
  fork(getNotificationOrderBreads),
];

class OrderBreadValidationManager extends ValidationManager {
  isValidCanOrderTime(params: {
    requiredBakingMinutes: number;
    canOrderTime: Date | undefined;
    lastPickupTime: Date | undefined;
  }) {
    const { requiredBakingMinutes, canOrderTime, lastPickupTime } = params;

    if (!canOrderTime) return;

    const now = dayjs(new Date());
    const bakedAt = dayjs(new Date()).add(requiredBakingMinutes, 'minute');
    const canOrderBy = dayjs(canOrderTime);
    const lastPickupAt = dayjs(lastPickupTime);
    const key = 'can_order_time';
    if (canOrderBy.isBefore(now)) {
      this.addErrors(key, 'ラストオーダー時刻が過去の時刻になっています。');
    } else if (requiredBakingMinutes && canOrderBy.isBefore(bakedAt)) {
      this.addErrors(key, 'ラストオーダー時刻が、焼き上がりの時刻より前になっています。');
    } else if (lastPickupTime && canOrderBy.isSameOrAfter(lastPickupAt)) {
      this.addErrors(key, 'ラストオーダー時刻が、パンを受け取れる最終時刻以後になっています。');
    }
  }

  isValidLastPickupTime(params: {
    requiredBakingMinutes: number;
    canOrderTime: Date | undefined;
    lastPickupTime: Date | undefined;
  }) {
    const { requiredBakingMinutes, canOrderTime, lastPickupTime } = params;

    if (!lastPickupTime || !requiredBakingMinutes || !canOrderTime) return;

    const now = dayjs(new Date());
    const bakedAt = dayjs(new Date()).add(requiredBakingMinutes, 'minute');
    const canOrderBy = dayjs(canOrderTime);
    const lastPickupAt = dayjs(lastPickupTime);
    const key = 'last_pickup_time';
    if (lastPickupAt.isBefore(now)) {
      this.addErrors(key, 'パンを受け取れる最終時刻が過去の時刻になっています。');
    } else if (lastPickupAt.isBefore(bakedAt)) {
      this.addErrors(key, 'パンを受け取れる最終時刻が、焼き上がりの時刻より前になっています。');
    } else if (lastPickupAt.isSameOrBefore(canOrderBy)) {
      this.addErrors(key, 'パンを受け取れる最終時刻が、ラストオーダー時刻以前になっています。');
    }
  }
}

export default orderBread;
